import { AgeGroupSchema } from "@aura-frontend/omnibus-az-client";
import * as z from "zod";

import {
  DEFAULT_AWAY_TEAM_COLOR,
  DEFAULT_HOME_TEAM_COLOR,
  DEFAULT_TEAM_COLOR,
  TEAM_COLORS,
} from "@/lib/constants";

// Re-export for backwards compatibility
export { DEFAULT_TEAM_COLOR, TEAM_COLORS };

export const gameTypeEnum = ["game", "bullpen", "batting_practice"] as const;

const ageGroupValues = AgeGroupSchema.enum;
export type GameType = (typeof gameTypeEnum)[number];

export const isPractice = (
  type: GameType,
): type is "bullpen" | "batting_practice" => {
  return type === "bullpen" || type === "batting_practice";
};

const baseOverviewSchema = z.object({
  field: z.string().min(1, "Field is required"),
  type: z.enum(gameTypeEnum, {
    required_error: "Type is required",
  }),
  // name and additionalNotes only apply to scheduled events; postGameStart
  // has no destination for them and the Overview hides them in live mode.
  name: z.string().optional(),
  // Required when scheduling; auto-populated and hidden when creating a
  // live (right-now) event. The Overview component enforces this in
  // schedule mode.
  startDate: z.date().optional(),
  startTime: z.string(),
  endTime: z.string().min(1, "End time is required"),
  additionalNotes: z
    .string()
    .max(1000, "Additional notes must be 1000 characters or less")
    .optional(),
  // Machine-scoped organization selection. Required only when the machine has
  // orgs available; enforced in Overview.tsx's flushValues logic per field count.
  orgId: z.string().nullable().optional(),
});

const battingPracticeFields = {
  team: z.string().min(1, "Team Roster is required"),
  teamName: z.string().optional(),
  teamColor: z.enum(TEAM_COLORS).default(DEFAULT_TEAM_COLOR),
};

const bullpenOverviewSchema = baseOverviewSchema.extend({
  type: z.literal("bullpen"),
  team: z.string().optional(),
  teamName: z.string().optional(),
});

const battingPracticeOverviewSchema = baseOverviewSchema.extend({
  type: z.literal("batting_practice"),
  ...battingPracticeFields,
});

const gameOverviewSchema = baseOverviewSchema.extend({
  type: z.literal("game"),
  homeTeam: z.string().min(1, "Home team is required"),
  awayTeam: z.string().min(1, "Away team is required"),
  homeTeamColor: z.enum(TEAM_COLORS).default(DEFAULT_HOME_TEAM_COLOR),
  awayTeamColor: z.enum(TEAM_COLORS).default(DEFAULT_AWAY_TEAM_COLOR),
});

const eventLedModeEnum = ["blocked", "stat_tracking", "umpire_assist"] as const;

export const overviewFormSchema = z.discriminatedUnion("type", [
  bullpenOverviewSchema,
  battingPracticeOverviewSchema,
  gameOverviewSchema,
]);

export type OverviewFormValues = z.infer<typeof overviewFormSchema>;

export const configurationFormSchema = z.object({
  // `null` is a valid choice — it represents the "Standby" option, which
  // deliberately leaves the LED board untouched (no /balltracker LED endpoint
  // fires on live start; `internal.led` is null on a scheduled event).
  ledBoardMode: z
    .enum(eventLedModeEnum, {
      required_error: "LED board mode is required",
    })
    .nullable(),

  sport: z.enum(["baseball", "softball"], {
    required_error: "Sport is required",
  }),
  level: z
    .enum(ageGroupValues, { required_error: "Level is required" })
    .optional(),
  moundDistance: z.coerce
    .number({ required_error: "Mound distance is required" })
    .positive("Mound distance must be positive"),
  baseDistance: z.coerce.number().nonnegative().optional(),
  infieldAtFoulLine: z.coerce.number().nonnegative().optional(),
  fenceFirstBase: z.coerce.number().nonnegative().optional(),
  fenceThirdBase: z.coerce.number().nonnegative().optional(),
  leftField: z.coerce.number().nonnegative().optional(),
  centerField: z.coerce.number().nonnegative().optional(),
  rightField: z.coerce.number().nonnegative().optional(),

  strikeZone: z.object({
    name: z.string(),
    bottomInches: z.number(),
    topInches: z.number(),
    sideFromCenterInches: z.number(),
    strikeZoneYInches: z.number().optional(),
    topIncludedExpansionInches: z.number().optional(),
    bottomIncludedExpansionInches: z.number().optional(),
    sideFromCenterIncludedExpansionInches: z.number().optional(),
    ageGroup: z.enum(ageGroupValues).nullable().optional(),
  }),
});

export type ConfigurationFormValues = z.infer<typeof configurationFormSchema>;

const gamePlayerSchema = z.object({
  uuid: z.string().min(1),
  firstName: z.string(),
  lastName: z.string(),
  birthdate: z.string(),
  jerseyNumber: z.string(),
  batHand: z.enum(["R", "L", "S"]),
  throwHand: z.enum(["R", "L", "S"]),
});

const gameTeamSchema = z.object({
  // Older games stored no team names, and an unnamed team's lineup still saves
  name: z.string(),
  uuid: z.string().min(1, "Team is required"),
  parentName: z.string().nullish(),
  players: z.array(gamePlayerSchema),
  currentLineup: z.array(gamePlayerSchema).default([]),
  // Lineups are optional: the form keeps no pitcher/catcher as null
  currentPitcher: gamePlayerSchema.nullish(),
  currentCatcher: gamePlayerSchema.nullish(),
});

export const lineupFormSchema = z.object({
  homeTeam: gameTeamSchema,
  awayTeam: gameTeamSchema,
});

export type LineupFormValues = z.infer<typeof lineupFormSchema>;

export type EventFormData = {
  overview: OverviewFormValues;
  configuration: ConfigurationFormValues;
  lineup?: LineupFormValues;
  /**
   * Games: the latest team picked for each side, from either the Overview or
   * the Lineup tab. Saved even when the lineup itself isn't complete.
   */
  gameTeams?: {
    home: { uuid: string; name: string };
    away: { uuid: string; name: string };
  };
  practiceTeamVisibilityGroup?: string;
  teamColors?: {
    home?: string;
    away?: string;
  };
};
